import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Box, Button, Heading, Menu, ResponsiveContext, Tab, Tabs, Text, TextInput } from "grommet";
import * as React from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import CustomButton from "../components/CustomButton";
import LinkAnchor from "../components/LinkAnchor";
import StyledModal from "../components/StyledModel";
import CreateMap from "./CreateMap";
import { MapCard } from "./MapCard";
import MapProfile from "./MapProfile";
import { getMaps, MapViewModel } from "./MapsStore";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
// @ts-ignore
const bulkPlus = <FontAwesomeIcon size="xl" icon={["fad", "layer-plus"]}></FontAwesomeIcon>;
// @ts-ignore
const mapPlus = <FontAwesomeIcon size="xl" icon={["fad", "users-medical"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const search = <FontAwesomeIcon icon={["fad", "search"]}></FontAwesomeIcon>;

export const MapRow = ({ map, children, size = "medium" }: { map: MapViewModel; children?: React.ReactElement; size?: string }) => {
    let BoxProps = {};

    const navigate = useNavigate();

    if (!children) {
        BoxProps = {
            hoverIndicator: "background-back",
            onClick: () => {
                navigate(`${map.id}`);
            },
        };
    }

    return (
        <Box key={map.id} background={"background"} pad={size} direction="row" gap="small" {...BoxProps}>
            <Box justify="center">
                <img src={map.logoUri} width="35px" alt="" />
            </Box>
            <Box justify="between" direction="row" width="100%">
                <Box justify="center">
                    <LinkAnchor path={`${map.id}`} label={map.name}></LinkAnchor>
                </Box>
                <Box justify="center">{children}</Box>
            </Box>
        </Box>
    );
};

export const MapTile = ({ map }: { map: MapViewModel }) => {
    return (
        <Link to={`/maps/${map.id}`} style={{ textDecoration: "none" }}>
            <Box key={map.id} background={"background-back"} pad="medium" gap="small" height="auto" justify="between" fill="vertical" hoverIndicator="background-front" onClick={() => {}}>
                <Box align="center" justify="center">
                    <img src={map.logoUri} width="25px" alt="" />
                </Box>
                <Box>
                    <Box align="center" justify="between">
                        {map.name}
                    </Box>
                </Box>
            </Box>
        </Link>
    );
};

export const MapBlock = ({ map, size, children }: { map: MapViewModel; size?: string; children?: React.ReactElement }) => {
    let { pathname } = useLocation();
    const navigate = useNavigate();

    const logoSize = size ?? "150px";

    return (
        <Link to={`/maps/${map.id}`} style={{ textDecoration: "none" }}>
            <Box key={map.id} background={"background-back"} pad="medium" gap="small" height="auto" justify="between" fill="vertical" hoverIndicator="background-front" onClick={() => {}}>
                <Box align="center" justify="center">
                    <img src={map.logoUri} width={logoSize} alt="" />
                </Box>
                <Box>
                    <Box align="center" justify="center">
                        {map.name}
                    </Box>
                    {children && children}
                </Box>
            </Box>
        </Link>
    );
};

export const MapMenu = ({ map }) => {
    const size = React.useContext(ResponsiveContext);
    const navigate = useNavigate();
    return (
        <>
            {size == "small" && (
                <Menu
                    items={[
                        {
                            label: "Profile",
                            onClick: () => {
                                navigate("/maps/" + map.id);
                            },
                        },
                        {
                            label: "Home Page",
                            onClick: () => {
                                //@ts-ignore
                                window.location = "/ue/maps/" + map.id;
                            },
                        },
                    ]}
                />
            )}
            {size != "small" && (
                <Box direction="row" gap="small">
                    <CustomButton primary path={"/maps/" + map.id} icon={edit} label="Profile"></CustomButton>
                    <CustomButton primary href={"/ue/maps/" + map.id} icon={idBadge} label="Home Page"></CustomButton>
                </Box>
            )}
        </>
    );
};

export const MapGrid = ({ maps }) => {
    const size = React.useContext(ResponsiveContext);

    const BracketGridProps = size == "small" ? { rows: ["auto"], columns: ["1/2", "1/2"] } : { rows: ["auto"], columns: ["1/4", "1/4", "1/4", "1/4"] };

    return (
        <>
            <Box direction="row" gap="medium" wrap justify="center">
                {maps.map((map) => (
                    <Box margin={{ bottom: "medium" }} key={map.id}>
                        <Link to={`${map.id}`} style={{ textDecoration: "none", color: "unset" }}>
                            <MapCard map={map}></MapCard>
                        </Link>
                    </Box>
                    // <MapBlock map={map} key={map.id}></MapBlock>
                ))}
            </Box>
        </>
    );
};

const MapsManagement = () => {
    const user = useUser();

    const [maps, setMaps] = React.useState([] as MapViewModel[]);
    const [filter, setFilter] = React.useState("");
    const [filteredMaps, setFilteredMaps] = React.useState([] as MapViewModel[]);
    let { pathname } = useLocation();

    React.useEffect(() => {
        getMaps(user.organization.id).then((maps) => {
            setMaps(maps);
        });
    }, [pathname, user]);

    React.useEffect(() => {
        if (filter) {
            setFilteredMaps(maps.filter((t) => t.name.toLowerCase().indexOf(filter.toLowerCase()) != -1));
        } else {
            setFilteredMaps(maps);
        }
    }, [maps, filter]);

    const [index, setIndex] = React.useState(0);
    const onActive = (nextIndex: number) => setIndex(nextIndex);

    const hasWritePermission = usePermission("sport:maps.write");

    const [showMapsCreateModal, setShowMapsCreateModal] = React.useState(false);
    const [showMapsBulkCreateModal, setShowMapsBulkCreateModal] = React.useState(false);

    return (
        <div>
            <Routes>
                <Route path={"create"} element={<>{hasWritePermission && <CreateMap />}</>}></Route>
                <Route path={":id/*"} element={<MapProfile />}></Route>
                <Route
                    path={""}
                    element={
                        <>
                            {/* <Heading size="small" textAlign="center" fill>
                                Maps
                            </Heading> */}
                            <Box direction="row" gap="small" justify="between">
                                {hasWritePermission && (
                                    <Box direction="row" gap="small">
                                        <CustomButton onClick={() => setShowMapsCreateModal(true)} icon={mapPlus} label="Add Map"></CustomButton>
                                    </Box>
                                )}
                                <Box direction="row" justify="end" gap="small" align="center">
                                    <Box pad="small">
                                        <TextInput icon={search} placeholder="Search..." value={filter} onChange={(e) => setFilter(e.target.value)}></TextInput>
                                    </Box>
                                    <Box pad="small" hoverIndicator="background-back" onClick={() => onActive(0)} round="5px">
                                        <FontAwesomeIcon size="lg" icon={["fad", "grid-2"]} />
                                    </Box>
                                    <Box pad="small" hoverIndicator="background-back" onClick={() => onActive(1)} round="5px">
                                        <FontAwesomeIcon size="lg" icon={["fad", "list"]} />
                                    </Box>
                                </Box>
                            </Box>
                            <Tabs activeIndex={index} onActive={onActive}>
                                <Tab>
                                    <MapGrid maps={filteredMaps}></MapGrid>
                                </Tab>
                                <Tab>
                                    <Box gap="small">
                                        {filteredMaps.map((map) => (
                                            <MapRow map={map} key={map.id}>
                                                <Box direction="row" gap="small">
                                                    <MapMenu map={map}></MapMenu>
                                                </Box>
                                            </MapRow>
                                        ))}
                                    </Box>
                                </Tab>
                            </Tabs>
                            <Box pad={{ top: "small" }}>
                                {maps.length != filteredMaps.length && (
                                    <Text>
                                        Showing {filteredMaps.length} of {maps.length}{" "}
                                        <Anchor href="#" onClick={() => setFilter("")}>
                                            Show All
                                        </Anchor>
                                    </Text>
                                )}
                            </Box>

                            <StyledModal isOpen={showMapsCreateModal} setIsOpen={() => setShowMapsCreateModal(false)}>
                                <CreateMap />
                            </StyledModal>
                        </>
                    }
                ></Route>
            </Routes>
        </div>
    );
};

export default MapsManagement;
